import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { NavigationContext } from '@context/NavigationContext'
import { PredictionContext } from '@context/PredictionContext'
import { ConsentContext } from '@context/ConsentContext'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyMode } from '@reducers/SurveyReducer'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { TabbedPane } from '@components/Survey/TabbedPane/TabbedPane'
import { Trans, useTranslation } from 'react-i18next'
import './ResultPage.scss'
import { usePreventBackNavigation } from '@hooks/usePreventBackNavigation'
import { APP_TYPE, AppType, isIntegratedApp, isLiteraryApp } from '@models/AppType'
import { GenderBookGoToWebsiteHeader } from '../GenderBookGoToWebsite/GenderBookGoToWebsiteHeader'
import ShareModal from '@hoc/WithMenu/ShareModal'
import { ResultPageHeader } from './ResultPageHeader'
import { ResultPageFooter } from './ResultPageFooter'
import { ResultPageFooterExternalApp } from './ResultPageFooterExternalApp'
import { LeoPanelCallout } from './LeoPanelCallout'
import { clearIntegrationData, getIntegrationData } from '@utils/integration'
import { useShare } from '@hooks/useShare'
import AppLoader from '@components/AppLoader/AppLoader'
import { PredictionData, PredictionResults } from '@models/Prediction/Prediction'
import {
  categoryHasMultipleSurveysFromSurvey,
  categoryUsesDsmpPrediction,
  categoryUsesProgressiveVoteModels,
  getCategory,
  hasNextSurveyInCategory,
  resolveResultVariant,
} from '@utils/categoryFlow'
import DsmpResultPage from '@components/Survey/Dsmp/DsmpResultPage'

function ResultPage() {
  usePreventBackNavigation()
  const { t, i18n } = useTranslation('Result')
  const currentLocale = (i18n.language ?? 'fr').substring(0, 2)
  const { setPrevHandler, setNextHandler } = useContext(NavigationContext)
  const {
    survey,
    resultVariant,
    questionnairePhase,
    hasAcknowledged,
    submitAnswers,
    setMode,
    setNextStep,
    submitCompleteSurvey,
    continueToSecondQuestionnaire,
  } = useContext(SurveyContext)
  const { prediction, results, sandboxedResults, dsmpQ1, dsmpQ2 } = useContext(PredictionContext)
  const { consentData, checkConsents } = useContext(ConsentContext)
  const category = getCategory(survey)
  const submitAttemptedRef = useRef(false)
  const isDsmpMultiSurvey =
    categoryUsesDsmpPrediction(category) && categoryHasMultipleSurveysFromSurvey(survey)
  const showDsmpResults = isDsmpMultiSurvey && (dsmpQ1 != null || dsmpQ2 != null)
  const isProgressiveVoteIntermediate =
    categoryUsesProgressiveVoteModels(category) && hasNextSurveyInCategory(survey)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const { datagotchiImgUrl, url } = useShare()
  const goToNextQuestionnaire = useCallback(() => {
    setIsLoading(true)
    continueToSecondQuestionnaire()
      .then(() => setNextStep(SurveyStep.Answer))
      .finally(() => setIsLoading(false))
  }, [continueToSecondQuestionnaire, setNextStep])

  const handleSelection = useCallback(() => {
    if (isProgressiveVoteIntermediate) {
      goToNextQuestionnaire()
      return
    }
    if (isIntegratedApp()) {
      setIsLoading(true)
      submitCompleteSurvey().then(() => {
        const params = getIntegrationData()
        clearIntegrationData()
        const convertedParams = new URLSearchParams(params)
        convertedParams.set('shareUrl', url || '')
        convertedParams.set('imageUrl', datagotchiImgUrl || '')
        window.open(`https://api.legeropinion.com/EndSurvey.aspx?${convertedParams.toString()}`, '_self')
      })
    } else {
      setNextStep(SurveyStep.Sandbox)
    }
  }, [
    isProgressiveVoteIntermediate,
    goToNextQuestionnaire,
    submitCompleteSurvey,
    url,
    datagotchiImgUrl,
    setNextStep,
  ])

  const actionLabel = `> ${
    isProgressiveVoteIntermediate
      ? t('choices.continue')
      : isIntegratedApp()
        ? t('choices.complete')
        : t('choices.sim')
  }`

  const currentText = useCurrentText(
    results,
    prediction,
    currentLocale,
    hasAcknowledged,
    isProgressiveVoteIntermediate,
    { label: actionLabel, onClick: handleSelection },
  )

  useEffect(() => {
    if (showDsmpResults) {
      return
    }
    setMode(SurveyMode.RESULTS)
    setPrevHandler(undefined)
    // Intermediate → next questionnaire ; final → simulator (or integrated complete).
    setNextHandler(() => handleSelection())

    return () => {
      setMode(SurveyMode.DEFAULT)
      setNextHandler(undefined)
    }
  }, [showDsmpResults, handleSelection, setMode, setPrevHandler, setNextHandler])

  useEffect(() => {
    if (!consentData) {
      void checkConsents()
    }
  }, [consentData, checkConsents])

  useEffect(() => {
    if (isDsmpMultiSurvey || submitAttemptedRef.current) {
      return
    }
    if (!consentData?.visitorId) {
      return
    }
    if (!hasAcknowledged || !prediction) {
      submitAttemptedRef.current = true
      void submitAnswers()
    }
  }, [isDsmpMultiSurvey, consentData?.visitorId, hasAcknowledged, prediction, submitAnswers])

  if (showDsmpResults) {
    return (
      <DsmpResultPage
        variant={resolveResultVariant(resultVariant, survey, questionnairePhase)}
      />
    )
  }

  const formattedPrediction = useMemo(
    () =>
      new Intl.NumberFormat(i18n.language, { style: 'percent', maximumFractionDigits: 1 }).format(
        prediction?.value ?? 0,
      ),
    [prediction],
  )

  const tabs = [
    {
      text: t('navigation.title'),
    },
  ]
  const navigationProgressText = t('navigation.progress', {
    abbr: prediction?.names?.acr?.[currentLocale] ?? '',
    percent: formattedPrediction,
  })
  const headerText = APP_TYPE !== AppType.LQUE ? navigationProgressText : undefined

  return (
    <div className="ResultPage-container">
      {isLoading ? (
        <AppLoader />
      ) : (
        <>
          <TabbedPane
            tabs={tabs}
            headerText={headerText}
            contentText={currentText}
            propClassName="Result"
            predictionResults={sandboxedResults ?? results}
          />
          <ShareModal />
        </>
      )}
    </div>
  )
}

type FooterAction = {
  label: string
  onClick: () => void
}

export function useCurrentText(
  results: PredictionResults | undefined,
  prediction: PredictionData | undefined,
  currentLocale: string,
  hasAcknowledged: boolean | undefined,
  isProgressiveVoteIntermediate = false,
  footerAction?: FooterAction,
) {
  const { t } = useTranslation('Result')

  const [currentText, setCurrentText] = useState<JSX.Element>()

  useEffect(() => {
    let components

    if (isIntegratedApp()) {
      components = {
        em: <em />,
        br: <span />,
        a: <a />,
        strong: <strong />,
        partner: <LeoPanelCallout />,
        button: <ResultPageHeader />,
        footer: <ResultPageFooterExternalApp />,
      }
    } else {
      components = {
        em: <em />,
        br: <br />,
        a: <a />,
        partner: isProgressiveVoteIntermediate ? <span /> : <LeoPanelCallout />,
        strong: <strong />,
        button: isLiteraryApp ? (
          <GenderBookGoToWebsiteHeader withShare results={results} gender={prediction?.names?.full[currentLocale]} />
        ) : (
          <ResultPageHeader />
        ),
        footer: (
          <ResultPageFooter
            variant={isProgressiveVoteIntermediate ? 'continue' : 'simulator'}
            actionLabel={footerAction?.label}
            onAction={footerAction?.onClick}
          />
        ),
      }
    }

    if (isProgressiveVoteIntermediate) {
      return setCurrentText(
        <Trans
          i18nKey={'Result:questionTextIntermediate'}
          values={{ target: prediction?.names?.full[currentLocale] }}
          components={components}
        />,
      )
    }

    if (!hasAcknowledged) {
      return setCurrentText(
        <Trans
          i18nKey={'Result:questionText'}
          values={{ target: prediction?.names?.full[currentLocale] }}
          components={components}
        />,
      )
    }
    setCurrentText(
      <Trans
        i18nKey={'Result:questionTextAck'}
        values={{ target: `_${prediction?.names?.full[currentLocale] ?? ''}_` }}
        components={components}
      />,
    )
  }, [
    hasAcknowledged,
    isProgressiveVoteIntermediate,
    prediction,
    currentLocale,
    footerAction?.label,
    footerAction?.onClick,
    results,
    t,
  ])

  return currentText
}

export default ResultPage
